import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { PATH } from "routes/paths";
import {
  ambassadorApi,
  setAmbassadorToken,
  REFERRAL_AMBASSADOR_CODE_NOT_VERIFIED,
  type ReferralAmbassadorTokenResult,
} from "api/ambassadorApi";

type Mode = "login" | "signup" | "verify";

const FALLBACK_ERROR_MESSAGE = "잠시 후 다시 시도해 주세요";

// 레퍼럴 앰버서더(인플루언서) 대시보드 로그인/가입/이메일 인증.
// login | signup | verify 3단계 상태머신 단일 페이지 — verify 는 signup 직후 또는 미인증 계정 로그인 시 진입한다.
export default function AmbassadorLogin() {
  const navigate = useNavigate();

  const [mode, setMode] = useState<Mode>("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  // NIST 권고: 비밀번호 표시 옵션 제공 (오타로 인한 실패·재설정 유도 감소)
  const [showPassword, setShowPassword] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const switchMode = (next: Mode) => {
    setMode(next);
    setError(null);
    setNotice(null);
    setVerificationCode("");
    setPasswordConfirm("");
    setShowPassword(false);
  };

  const completeLogin = (result?: ReferralAmbassadorTokenResult) => {
    if (!result?.accessToken) {
      setError(FALLBACK_ERROR_MESSAGE);
      return;
    }
    setAmbassadorToken(result.accessToken);
    navigate(PATH.ambassador, { replace: true });
  };

  const submit = async (action: () => Promise<void>) => {
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    setNotice(null);
    try {
      await action();
    } catch (e) {
      setError(FALLBACK_ERROR_MESSAGE);
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogin = () =>
    submit(async () => {
      const res = await ambassadorApi.login(email, password);
      if (res.code === 0) {
        completeLogin(res.result);
        return;
      }
      if (res.code === REFERRAL_AMBASSADOR_CODE_NOT_VERIFIED) {
        setMode("verify");
        setNotice(
          "이메일 인증이 완료되지 않았어요. 메일로 받은 인증 코드를 입력해 주세요."
        );
        return;
      }
      setError(res.message || FALLBACK_ERROR_MESSAGE);
    });

  const handleSignup = () =>
    submit(async () => {
      const res = await ambassadorApi.signup(email, password);
      if (res.code === 0) {
        setMode("verify");
        setNotice("인증 코드를 이메일로 보냈어요. 10분 안에 입력해 주세요.");
        return;
      }
      setError(res.message || FALLBACK_ERROR_MESSAGE);
    });

  const handleVerify = () =>
    submit(async () => {
      const res = await ambassadorApi.verify(email, password, verificationCode);
      if (res.code === 0) {
        completeLogin(res.result);
        return;
      }
      setError(res.message || FALLBACK_ERROR_MESSAGE);
    });

  const handleResend = () =>
    submit(async () => {
      const res = await ambassadorApi.resendVerification(email);
      if (res.code === 0) {
        setNotice("인증 코드를 다시 보냈어요.");
        return;
      }
      setError(res.message || FALLBACK_ERROR_MESSAGE);
    });

  const emailPasswordFilled = email.trim().length > 0 && password.length > 0;
  const passwordConfirmMismatch =
    mode === "signup" &&
    passwordConfirm.length > 0 &&
    password !== passwordConfirm;
  const canSubmit =
    !submitting &&
    (mode === "verify"
      ? verificationCode.trim().length > 0 && password.length > 0
      : mode === "signup"
        ? emailPasswordFilled &&
          password.length >= 8 &&
          password === passwordConfirm
        : emailPasswordFilled);

  const handleSubmit = () => {
    if (!canSubmit) return;
    if (mode === "login") handleLogin();
    else if (mode === "signup") handleSignup();
    else handleVerify();
  };

  const inputClassName =
    "w-full rounded-lg border border-gray-300 px-4 py-3 text-[15px] outline-none focus:border-gray-900";

  return (
    <div className="w-full h-full overflow-y-auto bg-gray-50">
      <div className="mx-auto w-full max-w-[400px] px-5 py-16">
        <h1 className="text-xl font-bold text-gray-900">
          디스턴싱 앰버서더 대시보드
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          {mode === "login" && "앰버서더 계정으로 로그인해 주세요."}
          {mode === "signup" &&
            "사전 등록된 앰버서더 이메일로만 가입할 수 있어요."}
          {mode === "verify" && "이메일로 받은 인증 코드를 입력해 주세요."}
        </p>

        <div className="mt-8 flex flex-col gap-3">
          <input
            className={inputClassName}
            type="email"
            inputMode="email"
            autoComplete="email"
            placeholder="이메일"
            value={email}
            disabled={mode === "verify"}
            onChange={(e) => setEmail(e.target.value)}
          />

          {mode !== "verify" && (
            <input
              className={inputClassName}
              type={showPassword ? "text" : "password"}
              autoComplete={
                mode === "signup" ? "new-password" : "current-password"
              }
              placeholder={
                mode === "signup" ? "비밀번호 (8자 이상)" : "비밀번호"
              }
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
            />
          )}

          {mode === "signup" && (
            <input
              className={inputClassName}
              type={showPassword ? "text" : "password"}
              autoComplete="new-password"
              placeholder="비밀번호 확인"
              value={passwordConfirm}
              onChange={(e) => setPasswordConfirm(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
            />
          )}

          {mode !== "verify" && (
            <label className="flex items-center gap-1.5 text-sm text-gray-500">
              <input
                type="checkbox"
                checked={showPassword}
                onChange={(e) => setShowPassword(e.target.checked)}
              />
              비밀번호 표시
            </label>
          )}

          {mode === "verify" && (
            <input
              className={inputClassName}
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="인증 코드 6자리"
              maxLength={6}
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
            />
          )}
        </div>

        {passwordConfirmMismatch && (
          <p className="mt-3 text-sm text-red-500">
            비밀번호가 일치하지 않아요.
          </p>
        )}
        {notice && <p className="mt-3 text-sm text-blue-600">{notice}</p>}
        {error && <p className="mt-3 text-sm text-red-500">{error}</p>}

        <button
          className="mt-6 w-full rounded-lg bg-gray-900 py-3 text-[15px] font-semibold text-white disabled:opacity-40"
          disabled={!canSubmit}
          onClick={handleSubmit}
        >
          {submitting
            ? "처리 중..."
            : mode === "login"
              ? "로그인"
              : mode === "signup"
                ? "가입하고 인증 메일 받기"
                : "인증하고 시작하기"}
        </button>

        <div className="mt-4 flex flex-col items-center gap-2 text-sm text-gray-500">
          {mode === "login" && (
            <button className="underline" onClick={() => switchMode("signup")}>
              처음이신가요? 앰버서더 가입
            </button>
          )}
          {mode === "signup" && (
            <button className="underline" onClick={() => switchMode("login")}>
              이미 계정이 있어요. 로그인
            </button>
          )}
          {mode === "verify" && (
            <>
              <button
                className="underline disabled:opacity-40"
                disabled={submitting}
                onClick={handleResend}
              >
                인증 코드 재발송
              </button>
              <button className="underline" onClick={() => switchMode("login")}>
                로그인으로 돌아가기
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
